import {
  useDeleteSpouses,
  useGetSpousDetails
} from 'common/queries-and-mutations/spouse';
import { Loader } from 'components/Loader';
import ConfirmModal from 'components/Modal/ConfirmModal';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import { Alert } from 'components/Toast';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import male from '../../../../../../assets/male-big.png';
import female from '../../../../../../assets/female-big.png';
import { StringFormater } from 'utils';
import Button from 'components/Form/Button';
import { Icon, Icons } from 'components/Icon';
import BankAsset from 'components/Cards/AssetsCards/BankAsset';
import RSAAsset from 'components/Cards/AssetsCards/RSAAsset';
import StockAsset from 'components/Cards/AssetsCards/StockAsset';
import BondAsset from 'components/Cards/AssetsCards/BondAsset';
import EmployeeAsset from 'components/Cards/AssetsCards/EmployeeAsset';
import PhysicalAssetCard from 'components/Cards/AssetsCards/PhysicalAsset';
import { Gender } from 'apps/user/types/user.enum.type';
import withCreatePortal from 'components/HOC/withCreatePortal';
import EditSpouseModal from './EditSpouseModal';

const EnhancedEditSpouseForm = withCreatePortal(EditSpouseModal);
const EnhancedConfirmModal = withCreatePortal(ConfirmModal);

export default function SpouseProfile() {
  const [showEditModal, setShowEditModal] = useState(false);
  const [userId, setUserId] = useState<number | null | undefined>(null);
  const [showAskToConfirm, setShowAskToConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { spouseId } = useParams();

  const { data, isLoading, refetch } = useGetSpousDetails({
    spouseId: Number(spouseId)
  });

  const {
    mutate: deleteSpouse,
    isLoading: isDeleting,
    isSuccess,
    isError
  } = useDeleteSpouses();

  useEffect(() => {
    if (isSuccess && !isError) {
      setIsSubmitting(false);
      setShowAskToConfirm(false);
      setUserId(null);
      refetch();
      toast(<Alert message="Spouse Successfully Deleted" type="success" />);
    }
  }, [isSuccess, isError, refetch]);

  return (
    <div>
      {isLoading && data ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <Breadcrumb title="Spouse Profile" />
          <div className="mt-1">
            <div className="mb-5 flex flex-row rounded bg-white">
              <div className="flex w-1/5 flex-col items-center justify-center border-r px-6">
                <div className="flex flex-col items-center justify-center gap-3">
                  <img src={data?.gender === Gender.MALE ? male : female} />
                  <p className="text-lg font-bold text-[#353535]">
                    {StringFormater(
                      `${data?.spouseFirstName} ${data?.spouseMiddleName} ${data?.spouseLastName}`
                    )}
                  </p>
                </div>
              </div>
              <div className="w-full">
                <div className="border-b border-[#E5E5E5]">
                  <div className="mt-3 grid grid-cols-4 gap-x-16 px-6">
                    <div className="col-span-1 px-8 py-6">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Name
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {`${data?.spouseFirstName} ${data?.spouseMiddleName} ${data?.spouseLastName}`}
                      </p>
                    </div>
                    <div className="col-span-1 px-8 py-4">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Phone Number
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {StringFormater(data?.phoneNumber)}
                      </p>
                    </div>
                    <div className="col-span-1 px-8 py-4">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Email Address
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {StringFormater(data?.emailAddress)}
                      </p>
                    </div>
                    <div className="col-span-1 px-8 py-4">
                      <p className="text-[10px] font-normal text-[#7A7C87]">
                        Home Address
                      </p>
                      <p className="mt-1 text-sm font-normal text-[#353535]">
                        {StringFormater(data?.homeAddress)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-x-16 px-6">
                  <div className="col-span-1 px-8 py-4">
                    <Button
                      variant="outline"
                      size="md"
                      className="mr-3 w-[15rem] rounded border border-[#FF7A00] bg-[#FFF8F2] p-[0.1rem] text-[#FF7A00]"
                      label="Edit Spouse Information"
                      icon={<Icon name={Icons.Edit} />}
                      onClick={() => {
                        setUserId(data?.id);
                        setShowEditModal(true);
                      }}
                    ></Button>
                  </div>
                  <div className="col-span-1 py-4">
                    <Button
                      variant="outline"
                      size="custom"
                      className="ml-10 w-[11rem] rounded border bg-[#E30000] p-[0.1rem] py-2 text-white"
                      label="Delete Spouse"
                      onClick={() => {
                        setUserId(data?.id);
                        setShowAskToConfirm(true);
                      }}
                    ></Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="my-3">
              <p className="text-base text-[#7A7C87]">Assigned Assets</p>
              {!data?.isAssigned ? (
                <div className="mt-10">
                  <p className="text-center font-circular text-lg font-bold">
                    No asset assigned
                  </p>
                </div>
              ) : (
                <div className="mt-2 h-[550px] space-y-3 overflow-auto scrollbar-hide">
                  {(data?.beneficiary.bankAllocations ?? []).map(asset => (
                    <div
                      className="mt-1 w-full rounded-md bg-white shadow-sm"
                      key={asset.id}
                    >
                      <BankAsset
                        bank={{
                          ...asset.bank,
                          percentageAlloted: asset.allottedPercentage!
                        }}
                      />
                    </div>
                  ))}
                  {(data?.beneficiary.pensionAccountAllocations ?? []).map(
                    asset => (
                      <div
                        className="mt-1 w-full rounded-md bg-white shadow-sm"
                        key={asset.id}
                      >
                        <RSAAsset
                          asset={{
                            ...asset.pension,
                            percentageAlloted: asset.allottedPercentage!
                          }}
                        />
                      </div>
                    )
                  )}
                  {(data?.beneficiary.stockShareAllocations ?? []).map(
                    asset => (
                      <div
                        className="mt-1 w-full rounded-md bg-white shadow-sm"
                        key={asset.id}
                      >
                        <StockAsset
                          asset={{
                            ...asset.stock,
                            percentageAlloted: asset.allottedPercentage!
                          }}
                        />
                      </div>
                    )
                  )}
                  {(data?.beneficiary.bondAllocations ?? []).map(asset => (
                    <div
                      className="mt-1 w-full rounded-md bg-white shadow-sm"
                      key={asset.id}
                    >
                      <BondAsset
                        asset={{
                          ...asset.bond,
                          percentageAlloted: asset.allottedPercentage!
                        }}
                      />
                    </div>
                  ))}
                  {(data?.beneficiary.employerAllocations ?? []).map(asset => (
                    <div
                      className="mt-1 w-full rounded-md bg-white shadow-sm"
                      key={asset.id}
                    >
                      <EmployeeAsset
                        asset={{
                          ...asset.employeeBenefit,
                          percentageAlloted: asset.allottedPercentage!
                        }}
                      />
                    </div>
                  ))}
                  {(data?.beneficiary.physicalAssetAllocations ?? []).map(
                    asset => (
                      <div
                        className="mt-1 w-full rounded-md bg-white shadow-sm"
                        key={asset.id}
                      >
                        <PhysicalAssetCard
                          asset={{
                            ...asset.physicalAsset,
                            percentageAlloted: asset.allottedPercentage!
                          }}
                        />
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {showAskToConfirm && (
        <EnhancedConfirmModal
          content={`Are you sure you want to remove the user? This can't be undone. Confirm if you want to continue.`}
          onConfirm={() => {
            deleteSpouse(userId!);
          }}
          onCancel={() => {
            setShowAskToConfirm(false);
          }}
          actionText="Yes"
          cancelText="No"
          title="Action Required"
          isSubmitting={isSubmitting || isDeleting}
        />
      )}

      {showEditModal && data && (
        <EnhancedEditSpouseForm
          onClose={() => {
            setShowEditModal(false);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            refetch();
          }}
          spouse={data}
        />
      )}
    </div>
  );
}
